import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface TakeOverBannerProps {
  onResume: () => void;
  /** Single-line icon+short-text rendering for phone landscape, where vertical space is already
   * scarce (header + toolbar + footer already consume roughly half a 390px-tall viewport). */
  compact?: boolean;
}

/** Persistent (non-auto-dismissing) banner shown while another tab holds write access to the same
 * task/experience — see useTaskTakeOver. Modeled on toast.tsx's variant/icon/styling convention,
 * but sticks around until the user acts instead of timing out. */
export default function TakeOverBanner({ onResume, compact }: TakeOverBannerProps) {
  const { t } = useTranslation();

  return (
    <div
      role='status'
      aria-live='polite'
      className={`flex shrink-0 items-center border-b border-ws-border bg-ws-warning-bg ${compact ? 'gap-2 px-3 py-1.5' : 'gap-3 px-4 py-2.5'}`}
    >
      <AlertTriangle aria-hidden='true' className={`shrink-0 text-ws-warning-fg ${compact ? 'h-4 w-4' : 'h-5 w-5'}`} />
      <span className={`min-w-0 flex-1 truncate font-medium text-ws-text ${compact ? 'text-xs' : 'text-sm'}`}>
        {compact ? t('takeOver.compactMessage') : t('takeOver.message')}
      </span>
      <button
        onClick={onResume}
        className={`shrink-0 rounded-md bg-ws-warning-fg font-medium text-white hover:opacity-90 ${compact ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'}`}
      >
        {t('takeOver.resume')}
      </button>
    </div>
  );
}
